import { useEffect, useState, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Image, Upload, Link2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ActusManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ACTUS', 'edit');
  const [actus, setActus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', active: true, image_url: '' });
  const [uploading, setUploading] = useState(false);
  const [imageMode, setImageMode] = useState('url');
  const fileRef = useRef();

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.getActus();
      setActus(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de charger les actualités');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', content: '', active: true, image_url: '' });
    setImageMode('url');
    setShowDialog(true);
  };

  const openEdit = (actu) => {
    setEditing(actu);
    setForm({ title: actu.title, content: actu.content, active: actu.active, image_url: actu.image_url || '' });
    setImageMode(actu.image_url ? 'url' : 'url');
    setShowDialog(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.uploadActuImage(fd);
      setForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Image téléchargée');
    } catch { toast.error('Erreur lors de l\'upload'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    try {
      if (editing) {
        await adminApi.updateActu(editing.id, form);
      } else {
        await adminApi.createActu(form);
      }
      setShowDialog(false);
      load();
      toast.success(editing ? 'Actualité modifiée' : 'Actualité créée');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
  };

  const remove = async (id) => {
    try {
      await adminApi.deleteActu(id);
      load();
      toast.success('Actualité supprimée');
    } catch { toast.error('Erreur'); }
  };

  if (loading) return (
    <AdminLayout title="Gestion des Actualités">
      <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div>
    </AdminLayout>
  );

  if (error) return (
    <AdminLayout title="Gestion des Actualités">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="actus-error">
        <AlertCircle className="mx-auto text-red-500 mb-2" size={24} />
        <p className="text-sm text-red-700 font-medium">{error}</p>
        <Button onClick={load} className="mt-4 bg-garden-800 hover:bg-garden-900 text-white rounded-full text-sm">Réessayer</Button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Gestion des Actualités">
      <div className="space-y-4" data-testid="actus-management">
        <div className="flex justify-between items-center">
          <p className="text-sm text-charcoal/70">{actus.length} actualité(s)</p>
          {canEdit && (
          <Button onClick={openNew} data-testid="add-actu-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
          )}
        </div>

        {actus.length === 0 ? (
          <div className="bg-white rounded-xl border border-garden-200 p-8 text-center">
            <p className="text-charcoal/50 text-sm">Aucune actualité. Cliquez sur "Ajouter" pour créer votre première actualité.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {actus.map((actu) => (
              <div key={actu.id} className="bg-white rounded-xl border border-garden-200 p-4 flex gap-4" data-testid={`actu-item-${actu.id}`}>
                {actu.image_url && (
                  <img
                    src={actu.image_url.startsWith('/') ? `${API}${actu.image_url}` : actu.image_url}
                    alt=""
                    className="w-24 h-24 object-cover rounded-xl shrink-0 bg-garden-100"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-heading font-semibold text-garden-800">{actu.title}</h3>
                      <p className="text-sm text-charcoal/70 mt-1 line-clamp-2">{actu.content}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {canEdit && (
                      <>
                      <button onClick={() => openEdit(actu)} className="p-1.5 text-garden-600 hover:text-garden-800 hover:bg-garden-50 rounded-lg" data-testid={`edit-actu-${actu.id}`}>
                        <Edit size={14} />
                      </button>
                      <button onClick={() => remove(actu.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-actu-${actu.id}`}>
                        <Trash2 size={14} />
                      </button>
                      </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${actu.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {actu.active ? 'Publiée' : 'Brouillon'}
                    </span>
                    {actu.image_url && <span className="text-xs text-charcoal/40 flex items-center gap-0.5"><Image size={10} /> Photo</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="text-garden-800">{editing ? 'Modifier' : 'Nouvelle'} actualité</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Titre *</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="border-garden-200 rounded-xl" placeholder="Nouvelle carte d'été !" data-testid="actu-title-input" />
              </div>
              <div>
                <Label className="text-sm">Contenu</Label>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="border-garden-200 rounded-xl" rows={4} placeholder="Décrivez l'actualité..." data-testid="actu-content-input" />
              </div>

              {/* Image section */}
              <div>
                <Label className="text-sm mb-2 block">Image (facultatif)</Label>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setImageMode('url')} className={`text-xs px-3 py-1 rounded-full border transition-colors ${imageMode === 'url' ? 'bg-garden-800 text-white border-garden-800' : 'border-garden-300 text-charcoal/70'}`}>
                    <Link2 size={12} className="inline mr-1" /> URL
                  </button>
                  <button onClick={() => setImageMode('upload')} className={`text-xs px-3 py-1 rounded-full border transition-colors ${imageMode === 'upload' ? 'bg-garden-800 text-white border-garden-800' : 'border-garden-300 text-charcoal/70'}`}>
                    <Upload size={12} className="inline mr-1" /> Télécharger
                  </button>
                </div>
                {imageMode === 'url' ? (
                  <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="border-garden-200 rounded-xl" placeholder="https://example.com/image.jpg" data-testid="actu-image-url-input" />
                ) : (
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                    <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl border-garden-200 w-full" data-testid="actu-image-upload-btn">
                      <Upload size={14} className="mr-1.5" /> {uploading ? 'Téléchargement...' : 'Choisir un fichier'}
                    </Button>
                  </div>
                )}
                {form.image_url && (
                  <div className="mt-2 relative">
                    <img src={form.image_url.startsWith('/') ? `${API}${form.image_url}` : form.image_url} alt="Aperçu" className="w-full h-32 object-cover rounded-xl" />
                    <button onClick={() => setForm({...form, image_url: ''})} className="absolute top-1 right-1 bg-red-500/80 text-white rounded-full p-1 hover:bg-red-600"><Trash2 size={12} /></button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} data-testid="actu-active-toggle" />
                <Label className="text-sm">Publier immédiatement</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Annuler</Button>
              <Button onClick={save} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="actu-save-btn">
                {editing ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
